if (!customElements.get('media-gallery')) {
	customElements.define('media-gallery', class MediaGallery extends HTMLElement {
		constructor() {
			super();
			this.elements = {
				mains: this.querySelectorAll('.product-media-main'),
				thumbnails: this.querySelectorAll('.product-media-thumbnail:not(.product-media-thumbnail-variant), .product-media-thumbnail:first-child'),
				slideshow: this.querySelector('.product-media-main-wrapper'),
			};

			if (!this.elements.thumbnails.length) {
				return;
			}

			this.elements.thumbnails.forEach((thumbnail) => {
				const mediaToggle = thumbnail.querySelector('button');
				if (!mediaToggle) {
					return;
				}
				mediaToggle.addEventListener('click', this.setActiveMedia.bind(this, mediaToggle.dataset.mediaId, false));
			});
		}

		connectedCallback() {
			this.elements.slideshow.style.display = 'block';
			this.enableNavigation = this.elements.slideshow.dataset.enableNavigation === 'true';
			
			this.flickity = new Flickity(this.elements.slideshow, {
				autoPlay: false,
				cellAlign: 'center',
				groupCells: false,
				contain: true,
				resize: true,
				draggable: true,
				prevNextButtons: false,
				cellSelector: '.product-media',
				initialIndex: 0,
				pageDots: false,
				wrapAround: true,
				accessibility: false,
				adaptiveHeight: true,
				watchCSS: true,
				on: {
					change: (index) => {
						if (this.flickity && this.flickity.cells && this.flickity.cells[index]) {
							const cell = this.flickity.cells[index];
							const element = cell.element;
							const mediaId = element ? element.dataset.mediaId : null;
							
							if (mediaId && element) {
								// Update active states directly from the Flickity cell
								this.updateActiveStatesFromCell(element, mediaId);
							}
							
							if (this.enableNavigation) {
								this.updateNavigationButtons();
							}
						}
					},
				},
			});

			if (this.enableNavigation) {
				this.initNavigation();
			}
		}

		initNavigation() {
			const prevButton = this.elements.slideshow.querySelector('.product-media-slider-nav-prev');
			const nextButton = this.elements.slideshow.querySelector('.product-media-slider-nav-next');

			if (prevButton && nextButton && this.flickity) {
				prevButton.addEventListener('click', (e) => {
					e.preventDefault();
					e.stopPropagation();
					this.navigateSlide('prev');
				});

				nextButton.addEventListener('click', (e) => {
					e.preventDefault();
					e.stopPropagation();
					this.navigateSlide('next');
				});

				// Update button states initially
				this.updateNavigationButtons();
			}
		}

		navigateSlide(direction) {
			if (!this.flickity || !this.elements.thumbnails || this.elements.thumbnails.length === 0) {
				return;
			}

			// Find the currently active thumbnail (same approach as setActiveMedia)
			const activeThumbnail = [...this.elements.thumbnails].find((thumb) => {
				return thumb.classList.contains('is-active');
			});

			if (!activeThumbnail) {
				return;
			}

			// Get the current index from thumbnails array
			const currentIndex = [...this.elements.thumbnails].indexOf(activeThumbnail);
			let newIndex;

			// Calculate new index (with wrap around)
			if (direction === 'prev') {
				newIndex = currentIndex > 0 ? currentIndex - 1 : this.elements.thumbnails.length - 1;
			} else {
				newIndex = currentIndex < this.elements.thumbnails.length - 1 ? currentIndex + 1 : 0;
			}

			// Get the new thumbnail and its mediaId
			const newThumbnail = this.elements.thumbnails[newIndex];
			if (!newThumbnail) {
				return;
			}

			const newMediaId = newThumbnail.dataset.mediaId;
			if (!newMediaId) {
				return;
			}

			// Use setActiveMedia to switch, which handles all the logic
			// Pass undefined as prepend to allow update even if already active
			this.setActiveMedia(newMediaId, undefined);
		}

		updateNavigationButtons() {
			if (!this.flickity || !this.flickity.cells) return;

			const prevButton = this.elements.slideshow.querySelector('.product-media-slider-nav-prev');
			const nextButton = this.elements.slideshow.querySelector('.product-media-slider-nav-next');

			if (prevButton && nextButton) {
				// For wrapAround mode, buttons are always enabled
				// But we can still update their visual state if needed
				const isFirst = this.flickity.selectedIndex === 0;
				const isLast = this.flickity.selectedIndex === this.flickity.cells.length - 1;

				// Since wrapAround is true, we don't disable buttons
				// But we can add a class for styling if needed
				prevButton.classList.toggle('at-start', isFirst && !this.flickity.options.wrapAround);
				nextButton.classList.toggle('at-end', isLast && !this.flickity.options.wrapAround);
			}
		}

		updateActiveStatesFromCell(activeCellElement, mediaId) {
			if (!activeCellElement || !mediaId) {
				return;
			}

			// Remove is-active from all main media
			this.elements.mains.forEach((main) => {
				main.classList.remove('is-active');
			});

			// Remove is-active from all thumbnails
			this.elements.thumbnails.forEach((thumb) => {
				thumb.classList.remove('is-active');
			});

			// Find the main media element that matches the active cell
			// The activeCellElement should be a .product-media-main element
			let activeMainMedia = null;
			
			// First, try to find by matching the element directly
			if (activeCellElement.classList.contains('product-media-main')) {
				activeMainMedia = activeCellElement;
			} else {
				// Fallback: find by mediaId
				activeMainMedia = [...this.elements.mains].find((element) => {
					return element.dataset.mediaId === mediaId.toString();
				});
			}

			// Add is-active to the new active main media
			if (activeMainMedia) {
				activeMainMedia.classList.add('is-active');
				this.playActiveMedia(activeMainMedia);
			}

			// Find and activate the corresponding thumbnail
			const thumbnail = [...this.elements.thumbnails].find((element) => {
				return element.dataset.mediaId === mediaId.toString();
			});
			
			if (thumbnail) {
				thumbnail.classList.add('is-active');
				// Only scroll if not triggered by navigation buttons
				if (!this.enableNavigation) {
					thumbnail.scrollIntoView({
						behavior: 'smooth',
						block: 'nearest',
					});
				}
			}

			// Update active index display
			const mains = Array.from(this.querySelectorAll('.product-media-main'));
			const activeIndex = mains.findIndex((el) => el.classList.contains('is-active'));
			const activeIndexElement = this.querySelector('.yg-active-index');
			if (activeIndexElement && activeIndex !== -1) {
				activeIndexElement.innerText = activeIndex + 1;
			}
		}

		setActiveMedia(mediaId, prepend) {
			const thumbnail = [...this.elements.thumbnails].find((element) => {
				return element.dataset.mediaId === mediaId.toString();
			});
			const isActive = thumbnail?.classList.contains('is-active');

			if (!thumbnail) {
				return;
			}

			// Only skip if it's already active and we're not forcing an update
			if (isActive && prepend !== undefined) {
				return;
			}

			const index = [...this.elements.thumbnails].indexOf(thumbnail);

			// Only call flickity.select if we're not already on this slide
			// This prevents infinite loops when navigation buttons trigger change events
			if (this.flickity && this.flickity.selectedIndex !== index) {
				this.flickity.select(index);
			}

			const activeMainMedia = [...this.elements.mains].find((element) => {
				return element.classList.contains('is-active');
			});
			
			// Update thumbnails
			this.elements.thumbnails.forEach((thumb) => {
				thumb.classList.remove('is-active');
			});
			
			// Update main media
			if (activeMainMedia) {
				activeMainMedia.classList.remove('is-active');
			}

			const nextMainMedia = this.elements.mains[index];
			if (nextMainMedia) {
				nextMainMedia.classList.add('is-active');
			}
			
			if (thumbnail) {
				thumbnail.classList.add('is-active');
			}
			
			if (nextMainMedia) {
				this.playActiveMedia(nextMainMedia);
			}

			if (prepend) {
				if (thumbnail) {
					thumbnail.parentElement.prepend(thumbnail);
				}
			} else if (thumbnail) {
				thumbnail.scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
				});
			}

			// Update active index display
			const mains = Array.from(this.querySelectorAll('.product-media-main'));
			const activeIndex = mains.findIndex((el) => el.classList.contains('is-active'));
			const activeIndexElement = this.querySelector('.yg-active-index');
			if (activeIndexElement && activeIndex !== -1) {
				activeIndexElement.innerText = activeIndex + 1;
			}
		}

		playActiveMedia(activeItem) {
			window.pauseAllMedia();
			const deferredMedia = activeItem.querySelector('deferred-media');

			if (deferredMedia) {
				deferredMedia.loadContent(false);
			}
		}
	});
}


if (!customElements.get('media-zoom')) {
	customElements.define('media-zoom', class MediaZoom extends HTMLElement {
		constructor() {
			super();

			this.mode = this.getAttribute('data-mode');

			this.zoomWidth = parseInt(this.getAttribute('data-zoom-width'), 10) || 150;
			this.zoomHeight = parseInt(this.getAttribute('data-zoom-height'), 10) || 150;

			this.lastEvent = null;
			this.onMouseEnter = this.onMouseEnter.bind(this);
			this.onMouseMove = this.onMouseMove.bind(this);
			this.onMouseLeave = this.onMouseLeave.bind(this);
			this.onClick = this.onClick.bind(this);
		}

		connectedCallback() {
			this.innerHTML += `<div class="media-zoom-zoom-area"></div>`;
			this.original = this.querySelector('img');
			this.zoomArea = this.querySelector('.media-zoom-zoom-area');

			this.original.addEventListener('mouseenter', this.onMouseEnter);
			this.original.addEventListener('mousemove', this.onMouseMove);
			this.original.addEventListener('mouseleave', this.onMouseLeave);

			if (this.mode === 'lightbox_zoom_hover') {
				this.original.addEventListener('click', this.onClick);
			}

			this.zoomArea.style.width = `${this.zoomWidth}px`;
			this.zoomArea.style.height = `${this.zoomHeight}px`;

			this.preview = document.createElement('div');
			this.preview.classList.add('media-zoom-preview-area', 'color-background-2');
			this.preview.innerHTML = '<div class="spinner"></div>';
			document.body.appendChild(this.preview);
		}

		disconnectedCallback() {
			this.original.removeEventListener('mouseenter', this.onMouseEnter);
			this.original.removeEventListener('mousemove', this.onMouseMove);
			this.original.removeEventListener('mouseleave', this.onMouseLeave);
			document.body.removeChild(this.preview);
		}

		onMouseEnter() {
			this.zoomArea.style.display = 'block';
			this.preview.style.display = 'block';

			const previewSize = `${this.original.offsetWidth + 25}px`;
			this.preview.style.width = previewSize;
			this.preview.style.height = previewSize;

			// Load larger image on first mouse enter
			if (!this.previewImg) {
				this.previewImg = new Image();
				this.previewImg.src = this.getAttribute('data-zoom');
				this.previewImg.onload = () => {
					const zoomScale = this.original.offsetWidth / this.zoomWidth;

					this.previewImg.style.width = `${(this.original.offsetWidth + 25) * zoomScale}px`;
					this.previewImg.style.height = `${(this.original.offsetHeight + 25) * zoomScale}px`;

					this.preview.innerHTML = '';
					this.preview.appendChild(this.previewImg);
					if (this.lastEvent) {
						this.original.dispatchEvent(new MouseEvent('mousemove', this.lastEvent));
					}
				};
			} else {
				// Set the correct preview image always on mouse enter
				const zoomScale = this.original.offsetWidth / this.zoomWidth;
				this.previewImg.style.width = `${(this.original.offsetWidth + 25) * zoomScale}px`;
				this.previewImg.style.height = `${(this.original.offsetHeight + 25) * zoomScale}px`;
			}
		}

		onMouseMove(e) {
			this.lastEvent = e;
			const { left, top, width, height } = this.original.getBoundingClientRect();

			const x = e.pageX - (window.pageXOffset + left);
			const y = e.pageY - (window.pageYOffset + top);

			const zoomX = Math.min(Math.max(x - this.zoomWidth / 2, 0), width - this.zoomWidth);
			const zoomY = Math.min(Math.max(y - this.zoomHeight / 2, 0), height - this.zoomHeight);

			this.zoomArea.style.left = `${zoomX}px`;
			this.zoomArea.style.top = `${zoomY}px`;

			this.preview.style.position = 'fixed';
			this.preview.style.left = `${left + width + 50}px`;
			this.preview.style.top = `${top - 14}px`;

			if (this.previewImg && this.previewImg.complete) {
				const previewX = (zoomX / width) * this.previewImg.width;
				const previewY = (zoomY / height) * this.previewImg.height;

				this.previewImg.style.transform = `translate(${-previewX}px, ${-previewY}px)`;
			}
		}

		onMouseLeave() {
			this.zoomArea.style.display = 'none';
			this.preview.style.display = 'none';
		}

		onClick() {
			const sectionId = this.getAttribute('data-modal-id');
			const modal = document.querySelector(`#ProductModal-${sectionId}`);

			if (modal) {
				modal.show(this);
			}
		}
	});
}
